// فرم کالا (SCR-STAFF-870؛ US-INV-001 تا 003): شناسه، نوع و دسته؛ واحد پایه، خرید و مصرف با ضرایب تبدیل (BR-INV-003)؛ ردیابی بچ و انقضا،
// چندمصرفی و مهلت پس از باز شدن؛ GTIN، IRC و بارکدها (BR-INV-004)؛ دسته مالیاتی و خدمت ProductSale برای کالای فروشی.
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InvItemRequest,
  type InvItemType,
  type InvItemUnit,
  type InvItemView,
  type InvSalesTaxCategory,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { applyServerErrors, CheckboxRHF, FormDialog, latinDigits, SelectRHF, TextFieldRHF, zRequiredText } from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { inventoryKeys, isSellable, ITEM_TYPES, ITEM_UNITS, useItemCategories } from './inventoryCommon';

const CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,31}$/;

const itemSchema = z
  .object({
    code: z.string().trim().regex(CODE_PATTERN, { error: 'errors:validation.inv_code' }),
    name: zRequiredText(200),
    brandName: z.string().max(200, { error: 'common:validation.too_long' }),
    manufacturer: z.string().max(200, { error: 'common:validation.too_long' }),
    categoryId: z.string(),
    type: z.enum(ITEM_TYPES as [InvItemType, ...InvItemType[]]),
    baseUnit: z.enum(ITEM_UNITS as [InvItemUnit, ...InvItemUnit[]]),
    purchaseUnit: z.string(),
    consumptionUnit: z.string(),
    conversions: z.array(z.object({ unit: z.string(), unitQuantity: z.string(), baseQuantity: z.string() })),
    tracksBatch: z.boolean(),
    tracksExpiry: z.boolean(),
    // AP-INT-04 (US-INV-080): کالای مشمول سامانه ردیابی کالای سلامت.
    tracksUid: z.boolean(),
    isMultiUse: z.boolean(),
    beyondUseHours: z.string(),
    salesTaxCategory: z.string(),
    saleServiceId: z.string(),
    gtin: z.string(),
    irc: z.string(),
    barcodes: z.string(),
    description: z.string().max(1000, { error: 'common:validation.too_long' }),
  })
  .superRefine((value, ctx) => {
    if (value.tracksExpiry && !value.tracksBatch) {
      ctx.addIssue({ code: 'custom', path: ['tracksBatch'], message: 'inv:item_form.errors.expiry_requires_batch' });
    }
    if (value.type === 'Medication' && !(value.tracksBatch && value.tracksExpiry)) {
      ctx.addIssue({ code: 'custom', path: ['tracksExpiry'], message: 'inv:item_form.errors.medication_tracking' });
    }
    if (value.isMultiUse && (!value.consumptionUnit || value.consumptionUnit === value.baseUnit)) {
      ctx.addIssue({ code: 'custom', path: ['consumptionUnit'], message: 'inv:item_form.errors.multi_use_unit' });
    }
    value.conversions.forEach((conversion, index) => {
      for (const field of ['unitQuantity', 'baseQuantity'] as const) {
        const parsed = Number(latinDigits(conversion[field]));
        if (!Number.isInteger(parsed) || parsed <= 0) {
          ctx.addIssue({ code: 'custom', path: ['conversions', index, field], message: 'common:validation.invalid' });
        }
      }
    });
  });
type ItemForm = z.input<typeof itemSchema>;

export interface ItemDialogProps {
  item: InvItemView | null;
  /** مقادیر پیش‌فرض ایجاد (مثلاً GTIN از اسکن). */
  initial?: Partial<ItemForm>;
  onClose: () => void;
  onSaved?: (item: InvItemView) => void;
}

function toForm(item: InvItemView | null, initial: Partial<ItemForm> | undefined): ItemForm {
  return {
    code: item?.code ?? '',
    name: item?.name ?? '',
    brandName: item?.brandName ?? '',
    manufacturer: item?.manufacturer ?? '',
    categoryId: item?.categoryId ?? '',
    type: item?.type ?? 'Consumable',
    baseUnit: item?.baseUnit ?? 'Piece',
    purchaseUnit: item && item.purchaseUnit !== item.baseUnit ? item.purchaseUnit : '',
    consumptionUnit: item && item.consumptionUnit !== item.baseUnit ? item.consumptionUnit : '',
    conversions: (item?.conversions ?? []).map((conversion) => ({
      unit: conversion.unit,
      unitQuantity: String(conversion.unitQuantity),
      baseQuantity: String(conversion.baseQuantity),
    })),
    tracksBatch: item?.tracksBatch ?? false,
    tracksExpiry: item?.tracksExpiry ?? false,
    tracksUid: item?.tracksUid ?? false,
    isMultiUse: item?.isMultiUse ?? false,
    beyondUseHours: item?.beyondUseHours === null || item?.beyondUseHours === undefined ? '' : String(item.beyondUseHours),
    salesTaxCategory: item?.salesTaxCategory ?? '',
    saleServiceId: item?.saleServiceId ?? '',
    gtin: item?.gtin ?? '',
    irc: item?.irc ?? '',
    barcodes: (item?.barcodes ?? []).join(', '),
    description: item?.description ?? '',
    ...initial,
  };
}

function toRequest(form: ItemForm, rowVersion?: number | string): InvItemRequest {
  const text = (value: string) => (value.trim() ? value.trim() : null);
  const sellable = isSellable(form.type);
  return {
    code: form.code.trim(),
    name: form.name.trim(),
    brandName: text(form.brandName),
    manufacturer: text(form.manufacturer),
    categoryId: form.categoryId || null,
    type: form.type,
    baseUnit: form.baseUnit,
    purchaseUnit: (form.purchaseUnit || null) as InvItemUnit | null,
    consumptionUnit: (form.consumptionUnit || null) as InvItemUnit | null,
    conversions: form.conversions.map((conversion) => ({
      unit: conversion.unit as InvItemUnit,
      unitQuantity: Number(latinDigits(conversion.unitQuantity)),
      baseQuantity: Number(latinDigits(conversion.baseQuantity)),
    })),
    tracksBatch: form.tracksBatch,
    tracksExpiry: form.tracksExpiry,
    tracksUid: form.tracksUid,
    isMultiUse: form.isMultiUse,
    beyondUseHours: form.isMultiUse && form.beyondUseHours.trim() ? Number(latinDigits(form.beyondUseHours)) : null,
    salesTaxCategory: sellable && form.salesTaxCategory ? (form.salesTaxCategory as InvSalesTaxCategory) : null,
    saleServiceId: sellable && form.saleServiceId.trim() ? form.saleServiceId.trim() : null,
    gtin: text(latinDigits(form.gtin)),
    irc: text(latinDigits(form.irc)),
    barcodes: form.barcodes
      .split(/[,،;\n]/)
      .map((value) => latinDigits(value).trim())
      .filter(Boolean),
    description: text(form.description),
    ...(rowVersion === undefined ? {} : { rowVersion: Number(rowVersion) }),
  };
}

export function ItemDialog({ item, initial, onClose, onSaved }: ItemDialogProps) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const categories = useItemCategories();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = item === null;
  const { control, handleSubmit, formState, setError } = useForm<ItemForm>({
    resolver: zodResolver(itemSchema),
    defaultValues: toForm(item, initial),
  });
  const conversions = useFieldArray({ control, name: 'conversions' });
  const [type, baseUnit, isMultiUse] = useWatch({ control, name: ['type', 'baseUnit', 'isMultiUse'] });
  const conversionUnits = useWatch({ control, name: 'conversions' }).map((conversion) => conversion.unit);
  const unitOptions = ITEM_UNITS.filter((unit) => unit !== baseUnit && conversionUnits.includes(unit)).map((value) => ({
    value,
    labelKey: `inv:unit.${value}`,
  }));

  const save = useMutation({
    mutationFn: (form: ItemForm) =>
      creating
        ? unwrap(
            tenantApi.POST('/api/v1/inventory/items', {
              body: toRequest(form),
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/inventory/items/{id}', {
              params: { path: { id: item.id } },
              body: toRequest(form, item.rowVersion),
            }),
          ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.items(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.categories(session?.tenantId) });
      onSaved?.(saved);
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: [
          'code',
          'name',
          'brandName',
          'manufacturer',
          'categoryId',
          'type',
          'baseUnit',
          'purchaseUnit',
          'consumptionUnit',
          'tracksBatch',
          'tracksExpiry',
          'tracksUid',
          'beyondUseHours',
          'salesTaxCategory',
          'saleServiceId',
          'gtin',
          'irc',
          'barcodes',
        ],
        codeFields: {
          'PHX-INV-0101': 'code',
          'PHX-INV-0201': 'code',
          'PHX-INV-0104': 'categoryId',
          'PHX-INV-0106': 'barcodes',
          'PHX-INV-0108': 'saleServiceId',
        },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-870-ITEM-FORM"
      titleKey={creating ? 'inv:item_form.create_title' : 'inv:item_form.edit_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="inv:item_form.code"
          helperKey={item?.codeLocked ? 'inv:item_form.code_locked' : 'inv:item_form.code_help'}
          required
          fullWidth
          disabled={Boolean(item?.codeLocked)}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'item-code' } }}
        />
        <TextFieldRHF control={control} name="name" labelKey="inv:item_form.name" required fullWidth autoFocus slotProps={{ htmlInput: { 'data-testid': 'item-name' } }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="brandName" labelKey="inv:item_form.brand" fullWidth />
        <TextFieldRHF control={control} name="manufacturer" labelKey="inv:item_form.manufacturer" fullWidth />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="type"
          labelKey="inv:item_form.type"
          required
          fullWidth
          options={ITEM_TYPES.map((value) => ({ value, labelKey: `inv:item_type.${value}` }))}
        />
        <SelectRHF
          control={control}
          name="categoryId"
          labelKey="inv:item_form.category"
          fullWidth
          options={[
            { value: '', labelKey: 'inv:item_form.no_category' },
            ...(categories.data?.items ?? []).map((category) => ({ value: category.id, label: category.name })),
          ]}
        />
      </Stack>

      <Typography variant="subtitle2">{t('inv:item_form.sections.units')}</Typography>
      <SelectRHF
        control={control}
        name="baseUnit"
        labelKey="inv:item_form.base_unit"
        helperKey="inv:item_form.base_unit_help"
        required
        fullWidth
        options={ITEM_UNITS.map((value) => ({ value, labelKey: `inv:unit.${value}` }))}
      />
      {conversions.fields.map((field, index) => (
        <Stack key={field.id} direction="row" spacing={1} alignItems="flex-start" data-testid={`conversion-row-${String(index)}`}>
          <TextFieldRHF
            control={control}
            name={`conversions.${index}.unitQuantity`}
            labelKey="inv:item_form.unit_quantity"
            slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': `conversion-unit-quantity-${String(index)}` } }}
          />
          <SelectRHF
            control={control}
            name={`conversions.${index}.unit`}
            labelKey="inv:item_form.conversion_unit"
            fullWidth
            options={ITEM_UNITS.filter((unit) => unit !== baseUnit).map((value) => ({ value, labelKey: `inv:unit.${value}` }))}
          />
          <Typography sx={{ pt: 2 }} aria-hidden>
            =
          </Typography>
          <TextFieldRHF
            control={control}
            name={`conversions.${index}.baseQuantity`}
            labelKey="inv:item_form.base_quantity"
            helperKey={`inv:unit.${baseUnit}`}
            slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': `conversion-base-quantity-${String(index)}` } }}
          />
          <IconButton aria-label={t('inv:item_form.remove_conversion')} onClick={() => conversions.remove(index)}>
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={() => conversions.append({ unit: ITEM_UNITS.find((unit) => unit !== baseUnit && !conversionUnits.includes(unit)) ?? 'Box', unitQuantity: '1', baseQuantity: '1' })}
        disabled={conversions.fields.length >= 10}
        data-testid="conversion-add"
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('inv:item_form.add_conversion')}
      </Button>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="purchaseUnit"
          labelKey="inv:item_form.purchase_unit"
          fullWidth
          options={[{ value: '', labelKey: 'inv:item_form.same_as_base' }, ...unitOptions]}
        />
        <SelectRHF
          control={control}
          name="consumptionUnit"
          labelKey="inv:item_form.consumption_unit"
          fullWidth
          options={[{ value: '', labelKey: 'inv:item_form.same_as_base' }, ...unitOptions]}
        />
      </Stack>

      <Typography variant="subtitle2">{t('inv:item_form.sections.tracking')}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <CheckboxRHF control={control} name="tracksBatch" labelKey="inv:item_form.tracks_batch" />
        <CheckboxRHF control={control} name="tracksExpiry" labelKey="inv:item_form.tracks_expiry" />
        <CheckboxRHF control={control} name="tracksUid" labelKey="inv:tracking.tracks_uid" />
        <CheckboxRHF control={control} name="isMultiUse" labelKey="inv:item_form.is_multi_use" />
      </Stack>
      {isMultiUse && (
        <TextFieldRHF
          control={control}
          name="beyondUseHours"
          labelKey="inv:item_form.beyond_use_hours"
          helperKey="inv:item_form.beyond_use_help"
          fullWidth
          slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': 'item-beyond-use' } }}
        />
      )}

      <Typography variant="subtitle2">{t('inv:item_form.sections.codes')}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="gtin" labelKey="inv:item_form.gtin" fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'item-gtin' } }} />
        <TextFieldRHF control={control} name="irc" labelKey="inv:item_form.irc" fullWidth slotProps={{ htmlInput: { dir: 'ltr' } }} />
      </Stack>
      <TextFieldRHF control={control} name="barcodes" labelKey="inv:item_form.barcodes" helperKey="inv:item_form.barcodes_help" fullWidth slotProps={{ htmlInput: { dir: 'ltr' } }} />

      {isSellable(type) && (
        <>
          <Typography variant="subtitle2">{t('inv:item_form.sections.sale')}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <SelectRHF
              control={control}
              name="salesTaxCategory"
              labelKey="inv:item_form.sales_tax"
              fullWidth
              options={[
                { value: '', labelKey: 'inv:item_form.no_value' },
                { value: 'Taxable', labelKey: 'inv:sales_tax.Taxable' },
                { value: 'Exempt', labelKey: 'inv:sales_tax.Exempt' },
              ]}
            />
            <TextFieldRHF control={control} name="saleServiceId" labelKey="inv:item_form.sale_service" helperKey="inv:item_form.sale_service_help" fullWidth slotProps={{ htmlInput: { dir: 'ltr' } }} />
          </Stack>
        </>
      )}
      <TextFieldRHF control={control} name="description" labelKey="inv:item_form.description" fullWidth multiline minRows={2} />
    </FormDialog>
  );
}
